import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useHydrateAuth } from '@/features/auth/hooks';
import { useAuthStore } from '@/features/auth/store';
import { FeatureGate } from '@/features/features/FeatureGate';
import { PageLoader } from '@/components/ui/Loading';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';

// Every page is code-split. Previously all 25 pages landed in a single
// ~1.5 MB index chunk, so an attendant on a phone downloaded the admin
// console + owner reports just to see their shift card. Now each route
// is its own chunk fetched on first navigation, with <Suspense> below
// showing PageLoader while the chunk streams in.
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const OwnerDashboardPage = lazy(() => import('@/pages/OwnerDashboardPage'));
const EmployeeDashboardPage = lazy(() => import('@/pages/EmployeeDashboardPage'));
const ShiftsListPage = lazy(() => import('@/pages/ShiftsListPage'));
const ShiftDetailPage = lazy(() => import('@/pages/ShiftDetailPage'));
const OwnerReportsPage = lazy(() => import('@/pages/OwnerReportsPage'));
const OwnerPricesPage = lazy(() => import('@/pages/OwnerPricesPage'));
const OwnerEmployeesPage = lazy(() => import('@/pages/OwnerEmployeesPage'));
const OwnerExpensesPage = lazy(() => import('@/pages/OwnerExpensesPage'));
const OwnerSetupPage = lazy(() => import('@/pages/OwnerSetupPage'));
const OwnerAuditLogPage = lazy(() => import('@/pages/OwnerAuditLogPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const WalkthroughPage = lazy(() => import('@/pages/WalkthroughPage'));
const AttendancePage = lazy(() => import('@/pages/AttendancePage'));
const CreditCustomersPage = lazy(() => import('@/pages/CreditCustomersPage'));
const InventoryPage = lazy(() => import('@/pages/InventoryPage'));
const PayrollPage = lazy(() => import('@/pages/PayrollPage'));
const SampleStickersPage = lazy(() => import('@/pages/SampleStickersPage'));
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const AdminTenantsPage = lazy(() => import('@/pages/admin/AdminTenantsPage'));
const AdminNewTenantPage = lazy(() => import('@/pages/admin/AdminNewTenantPage'));
const AdminTenantDetailPage = lazy(() => import('@/pages/admin/AdminTenantDetailPage'));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'));
const AdminAuditLogPage = lazy(() => import('@/pages/admin/AdminAuditLogPage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Employees are blocked from owner-only sections; they bounce to their dashboard. */
function OwnerOnly({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'employee') return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Only platform admins may access /admin/*. Everyone else bounces home. */
function PlatformAdminOnly({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isPlatformAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Home page varies by role:
 *   - Platform admin → /admin (their console is the meaningful landing;
 *     the owner dashboard would show "no pumps" since SysAdmin tenant is
 *     plumbing, not a real petrol pump).
 *   - Employee → mobile-first shift dashboard.
 *   - Owner / manager → rich KPI dashboard.
 */
function HomeRouter() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.isPlatformAdmin) return <Navigate to="/admin" replace />;
  if (user.role === 'employee') return <EmployeeDashboardPage />;
  return <OwnerDashboardPage />;
}

function AppShell({ children }: { children: React.ReactNode }) {
  const hydrate = useHydrateAuth();
  if (hydrate.isLoading) {
    return <PageLoader label="Signing you in…" />;
  }
  return (
    <>
      <ImpersonationBanner />
      {children}
    </>
  );
}

export default function App() {
  return (
    <AppShell>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HomeRouter />
              </ProtectedRoute>
            }
          />

          {/* Shifts — /shifts list is owner/manager only; /shifts/:id detail
              works for employees too (they see their action forms on shifts
              they're on, read-only otherwise). */}
          <Route
            path="/shifts"
            element={
              <OwnerOnly>
                <ShiftsListPage />
              </OwnerOnly>
            }
          />
          <Route
            path="/shifts/:id"
            element={
              <ProtectedRoute>
                <ShiftDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Owner + manager sections */}
          <Route
            path="/reports"
            element={
              <OwnerOnly>
                <OwnerReportsPage />
              </OwnerOnly>
            }
          />
          <Route
            path="/expenses"
            element={
              <OwnerOnly>
                <OwnerExpensesPage />
              </OwnerOnly>
            }
          />
          <Route
            path="/employees"
            element={
              <OwnerOnly>
                <OwnerEmployeesPage />
              </OwnerOnly>
            }
          />
          <Route
            path="/attendance"
            element={
              <OwnerOnly>
                <AttendancePage />
              </OwnerOnly>
            }
          />
          <Route
            path="/credit-customers"
            element={
              <OwnerOnly>
                <FeatureGate feature="credit" featureLabel="Credit customers">
                  <CreditCustomersPage />
                </FeatureGate>
              </OwnerOnly>
            }
          />
          <Route
            path="/inventory"
            element={
              <OwnerOnly>
                <FeatureGate feature="inventory" featureLabel="Inventory">
                  <InventoryPage />
                </FeatureGate>
              </OwnerOnly>
            }
          />
          <Route
            path="/payroll"
            element={
              <OwnerOnly>
                <FeatureGate feature="payroll" featureLabel="Payroll">
                  <PayrollPage />
                </FeatureGate>
              </OwnerOnly>
            }
          />
          <Route
            path="/samples"
            element={
              <OwnerOnly>
                <FeatureGate feature="samples" featureLabel="Sample stickers">
                  <SampleStickersPage />
                </FeatureGate>
              </OwnerOnly>
            }
          />
          <Route
            path="/prices"
            element={
              <OwnerOnly>
                <OwnerPricesPage />
              </OwnerOnly>
            }
          />
          <Route
            path="/setup"
            element={
              <OwnerOnly>
                <OwnerSetupPage />
              </OwnerOnly>
            }
          />
          <Route
            path="/audit"
            element={
              <OwnerOnly>
                <OwnerAuditLogPage />
              </OwnerOnly>
            }
          />

          {/* All roles: profile edit + password change. */}
          <Route
            path="/me/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* All roles: role-specific "getting started" walkthrough. */}
          <Route
            path="/walkthrough"
            element={
              <ProtectedRoute>
                <WalkthroughPage />
              </ProtectedRoute>
            }
          />

          {/* Platform admin console — separate route tree with its own layout. */}
          <Route
            path="/admin"
            element={
              <PlatformAdminOnly>
                <AdminDashboardPage />
              </PlatformAdminOnly>
            }
          />
          <Route
            path="/admin/tenants"
            element={
              <PlatformAdminOnly>
                <AdminTenantsPage />
              </PlatformAdminOnly>
            }
          />
          <Route
            path="/admin/tenants/new"
            element={
              <PlatformAdminOnly>
                <AdminNewTenantPage />
              </PlatformAdminOnly>
            }
          />
          <Route
            path="/admin/tenants/:id"
            element={
              <PlatformAdminOnly>
                <AdminTenantDetailPage />
              </PlatformAdminOnly>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PlatformAdminOnly>
                <AdminUsersPage />
              </PlatformAdminOnly>
            }
          />
          <Route
            path="/admin/audit-log"
            element={
              <PlatformAdminOnly>
                <AdminAuditLogPage />
              </PlatformAdminOnly>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}
